import React, { useState, useEffect } from 'react';
import './DocumentViewer.css';

// Google Docs API to fetch document content
// Note: Requires Google Docs API enabled and proper OAuth setup
async function fetchGoogleDocContent(docId, accessToken) {
  // Using Google Docs API to fetch document as HTML
  const response = await fetch(
    `https://docs.googleapis.com/v1/documents/${docId}?fields=body(content(paragraph,table)),title`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch document');
  }

  const data = await response.json();
  return data;
}

// Simple HTML converter for Google Docs content
function convertDocToHtml(doc) {
  let html = '';
  const body = doc.body || {};
  const content = body.content || [];

  html += `<h1>${doc.title || 'Document'}</h1>`;

  content.forEach(element => {
    if (element.paragraph) {
      const para = element.paragraph;
      let text = '';
      
      if (para.elements) {
        para.elements.forEach(el => {
          if (el.textRun) {
            let runText = el.textRun.content;
            const style = el.textRun.textStyle || {};
            
            if (style.bold) runText = `<strong>${runText}</strong>`;
            if (style.italic) runText = `<em>${runText}</em>`;
            if (style.underline) runText = `<u>${runText}</u>`;
            if (style.link) runText = `<a href="${style.link.url}">${runText}</a>`;
            
            text += runText;
          }
        });
      }

      // Check paragraph style for heading
      const headingStyle = para.paragraphStyle?.namedStyleType;
      if (headingStyle === 'HEADING_1') {
        html += `<h2>${text}</h2>`;
      } else if (headingStyle === 'HEADING_2') {
        html += `<h3>${text}</h3>`;
      } else if (headingStyle === 'HEADING_3') {
        html += `<h4>${text}</h4>`;
      } else {
        html += `<p>${text}</p>`;
      }
    }
  });

  return html;
}

function DocumentViewer({ doc, onClose }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadDoc() {
      if (!doc?.id) {
        setError('No document specified');
        setLoading(false);
        return;
      }

      try {
        // Get the access token from Firebase auth
        const authToken = await doc.auth?.currentUser?.getIdToken();
        
        if (!authToken) {
          setError('Please sign in again to view this document');
          setLoading(false);
          return;
        }

        const docData = await fetchGoogleDocContent(doc.id, authToken);
        const html = convertDocToHtml(docData);
        setContent(html);
      } catch (err) {
        console.error('Error loading document:', err);
        setError('Failed to load document. Make sure it\'s shared with "Anyone with link can view"');
      } finally {
        setLoading(false);
      }
    }

    loadDoc();
  }, [doc]);

  return (
    <div className="document-viewer">
      <div className="document-viewer-header">
        <button className="back-btn" onClick={onClose}>
          ← Back to Portal
        </button>
        <h2 className="document-title">{doc?.name || 'Document'}</h2>
        <div className="header-spacer"></div>
      </div>

      <div className="document-content">
        {loading && (
          <div className="document-loading">
            <div className="loading-spinner"></div>
            <p>Loading document...</p>
          </div>
        )}

        {error && (
          <div className="document-error">
            <p>{error}</p>
            <p className="help-text">
              Make sure the document is shared with "Anyone with the link can view"
            </p>
          </div>
        )}

        {content && !loading && !error && (
          <div 
            className="document-body"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}
      </div>
    </div>
  );
}

export default DocumentViewer;